/* ============================================================
   Home App — app-level styles on top of the design system.

   Everything visual comes from the design-system tokens. This file only
   handles the things a real installed app needs that a 392x812 prototype
   frame did not: full-viewport layout, iOS safe areas, and the two
   keyframe animations the prototype declared inline.
   ============================================================ */

@import './tokens/fonts.css';
@import './tokens/colors.css';
@import './tokens/typography.css';
@import './tokens/spacing.css';
@import './tokens/shadows.css';
@import './tokens/base.css';

html,
body,
#root {
  height: 100%;
}

body {
  /* The prototype canvas colour. On a real phone this is the app background
     behind the safe areas, so it must be the themed canvas, not a fixed hex. */
  background: var(--bg);
  overscroll-behavior-y: none;
  -webkit-tap-highlight-color: transparent;
}

/* The design system sets --tracking-tight on all headings, but its own
   readme says never to apply tight tracking to Japanese text. Headings in
   this app are Japanese-first, so opt back out and let the individual Latin
   display headings ask for it via .tracking-tight. */
h1,
h2,
h3,
h4,
h5,
h6 {
  letter-spacing: var(--tracking-normal);
}

.tracking-tight {
  letter-spacing: var(--tracking-tight);
}

/* Japanese body copy needs more line height than Latin (--leading-cjk). */
.cjk {
  line-height: var(--leading-cjk);
}

/* Scroll areas hide their scrollbar the way the prototype's .scr did, while
   staying keyboard- and touch-scrollable. */
.scroll-area {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.scroll-area::-webkit-scrollbar {
  width: 0;
  height: 0;
}

/* Numeric / tabular figures — times, counts, amounts. */
.numeric {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
}

button {
  font-family: inherit;
}

input,
textarea,
select {
  font-family: inherit;
  font-size: var(--text-md); /* keeps iOS Safari from zooming on focus */
  color: inherit;
}

/* ---- Motion (from the prototype's inline <style>) ---- */
@keyframes sheetUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
@keyframes scrimIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* The design system is explicit about being reduced-motion friendly. */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
